import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box, 
  TextField, 
  IconButton,
  Button,
  Tooltip,
  alpha,
  useTheme,
  Chip,
  Typography,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Paper,
  Menu,
  MenuItem,
  Slider,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Card
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  AccessTime as AccessTimeIcon,
  FormatSize as FontSizeIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useEditor } from '../../context/EditorContext';

// Minimal toolbar - keep buttons in original position
const MinimalToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.25, 2),
  minHeight: '36px',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: theme.palette.mode === 'dark' 
    ? 'rgba(13, 17, 23, 0.8)' // Match GitHub dark editor tone
    : alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
}));

// Clean text area - match docs editor styling
const CleanTextArea = styled('textarea')(({ theme, fontSize = 16, isDark }) => ({
  width: '100%',
  maxWidth: '800px', // Match docs editor width
  margin: '0 auto', // Center like docs
  minHeight: 'calc(100vh - 120px)', // Full height minus header space
  height: 'auto',
  border: 'none',
  outline: 'none',
  resize: 'vertical',
  padding: '40px 60px', // Match docs editor padding
  fontSize: `${fontSize}px`,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', // Quip font stack
  lineHeight: 1.65, // Match docs line height
  background: isDark ? '#0d1117' : '#ffffff', // GitHub dark theme option
  borderRadius: '8px', // Match docs border radius
  boxShadow: isDark 
    ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
    : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)', // Match docs shadow
  color: theme.palette.text.primary,
  overflow: 'hidden',
  '&::placeholder': {
    color: alpha(theme.palette.text.secondary, 0.5),
    fontSize: '1rem',
  },
}));

// Markdown preview container
const MarkdownPreview = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p': {
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.7,
  },
  '& code': {
    background: alpha(theme.palette.primary.main, 0.1),
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.9em',
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
  },
  '& pre': {
    background: alpha(theme.palette.background.paper, 0.5),
    borderRadius: 8,
    padding: theme.spacing(2),
    overflow: 'auto',
    margin: theme.spacing(2, 0),
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(2, 0),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  '& ul, & ol': {
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
  },
  '& li': {
    marginBottom: theme.spacing(0.5),
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: '4px',
  },
}));

// Compact action button
const ActionButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  background: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
}));

function SnippetEditor({
  content,
  title,
  isPrivate,
  expirationDays,
  onContentChange,
  onTitleChange,
  onPrivateChange,
  onExpirationChange,
  saving = false,
  selectedTags = [],
  selectedSummary,
  onSelectTags,
  onSelectSummary,
  showAISuggestions = false,
  // AI callback props
  onSelectTitle,
  onClearTitle,
  onClearTags,
  onClearSummary,
  onRemoveTag,
  onClearAllSelections,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark'; // Get dark mode state
  const textAreaRef = useRef(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const { 
    setEditorContent, 
    editorTitle, 
    setEditorTitle, 
    selectedTags: contextTags, 
    selectedSummary: contextSummary,
    setParentCallbacks 
  } = useEditor(); // Add editor context
  
  // Editor state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState(0); // 0: Edit, 1: Preview, 2: Split
  const [markdownMode, setMarkdownMode] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  // Remove showAISuggestions state since it's now a prop
  
  // Auto-focus effect and sync initial content
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
    // Sync initial content with editor context
    if (content) {
      setEditorContent(content);
    }
    // Register parent callbacks
    setParentCallbacks({
      onTitleChange,
      onPrivateChange,
      onExpirationChange,
    });
  }, [content, setEditorContent, setParentCallbacks, onTitleChange, onPrivateChange, onExpirationChange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey)) {
        switch (event.key) {
          case 'e':
            event.preventDefault();
            if (textAreaRef.current) {
              textAreaRef.current.focus();
            }
            break;
          case 'm':
            event.preventDefault();
            setMarkdownMode(!markdownMode);
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [markdownMode]);

  // Auto-resize textarea when content changes
  useEffect(() => {
    if (textAreaRef.current) {
      const textarea = textAreaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(textarea.scrollHeight, window.innerHeight * 0.6) + 'px';
    }
  }, [content]);

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    onContentChange(newContent);
    setEditorContent(newContent); // Sync with editor context
    
    // Auto-resize textarea to fit content
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(textarea.scrollHeight, window.innerHeight * 0.6) + 'px';
  };

  const handleTitleChange = (newTitle) => {
    onTitleChange(newTitle);
    setEditorTitle(newTitle); // Sync with editor context
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSettingsOpen = (event) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleExpirationChange = (days) => {
    onExpirationChange(days);
    handleSettingsClose();
  };

  const getExpirationLabel = () => {
    switch (expirationDays) {
      case 1: return '1 day';
      case 7: return '7 days';
      case 30: return '30 days';
      case 90: return '90 days';
      case 180: return '180 days';
      default: return '7 days';
    }
  };

  const insertMarkdown = (syntax) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText = '';
    switch (syntax) {
      case 'bold':
        newText = `**${selectedText || 'bold text'}**`;
        break;
      case 'italic':
        newText = `*${selectedText || 'italic text'}*`;
        break;
      case 'code':
        newText = selectedText.includes('\n') 
          ? `\`\`\`\n${selectedText || 'code'}\n\`\`\``
          : `\`${selectedText || 'code'}\``;
        break;
      case 'quote':
        newText = `> ${selectedText || 'quote'}`;
        break;
      case 'list':
        newText = `- ${selectedText || 'list item'}`;
        break;
      case 'numberedList':
        newText = `1. ${selectedText || 'list item'}`;
        break;
      case 'link':
        newText = `[${selectedText || 'link text'}](url)`;
        break;
      case 'image':
        newText = `![${selectedText || 'alt text'}](image-url)`;
        break;
      default:
        return;
    }

    const newContent = content.substring(0, start) + newText + content.substring(end);
    onContentChange(newContent);
    
    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + newText.length, start + newText.length);
    }, 0);
  };

  const getPlaceholder = () => {
    return "Start typing your content here...";
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Clean Toolbar */}
      <MinimalToolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Edit tab only */}
          <Tabs 
            value={viewMode === 0 ? 0 : 0} // Always show Edit as active when not in preview
            sx={{ minHeight: 'auto' }}
          >
            <Tab 
              icon={<EditIcon fontSize="small" />} 
              onClick={() => setViewMode(0)}
              sx={{ minHeight: 'auto', py: 1, minWidth: 'auto' }}
            />
          </Tabs>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Text Size */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            <FontSizeIcon fontSize="small" color="action" />
            <Slider
              value={fontSize}
              onChange={(e, newValue) => setFontSize(newValue)}
              min={12}
              max={24}
              step={1}
              size="small"
              sx={{ width: 80 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
              {fontSize}px
            </Typography>
          </Box>

          {/* Public/Private */}
          <Chip
            icon={isPrivate ? <LockIcon /> : <PublicIcon />}
            label={isPrivate ? 'Private' : 'Public'}
            onClick={() => onPrivateChange(!isPrivate)}
            size="small"
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />

          {/* Expiry Days */}
          <Chip
            icon={<AccessTimeIcon />}
            label={getExpirationLabel()}
            onClick={handleSettingsOpen}
            size="small"
            variant="outlined"
            sx={{ cursor: 'pointer' }}
          />

          {/* Markdown */}
          <FormControlLabel
            control={
              <Switch
                checked={markdownMode}
                onChange={(e) => setMarkdownMode(e.target.checked)}
                size="small"
              />
            }
            label="Markdown"
            sx={{ mr: 1 }}
          />

          {/* Preview tab - only when markdown is enabled */}
          {markdownMode && (
            <Tabs 
              value={viewMode === 1 ? 0 : -1} // Show as active when in preview mode
              sx={{ minHeight: 'auto' }}
            >
              <Tab 
                icon={<PreviewIcon fontSize="small" />} 
                onClick={() => setViewMode(1)}
                sx={{ minHeight: 'auto', py: 1, minWidth: 'auto' }}
              />
            </Tabs>
          )}
        </Box>

        {/* Empty right side - space for AI toggle */}
        <Box sx={{ flex: 1 }} />

        {/* Settings Menu */}
        <Menu
          anchorEl={settingsAnchor}
          open={Boolean(settingsAnchor)}
          onClose={handleSettingsClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 160,
            },
          }}
        >
          <MenuItem onClick={() => handleExpirationChange(1)}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            1 day
          </MenuItem>
          <MenuItem onClick={() => handleExpirationChange(7)}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            7 days
          </MenuItem>
          <MenuItem onClick={() => handleExpirationChange(30)}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            30 days
          </MenuItem>
          <MenuItem onClick={() => handleExpirationChange(90)}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            90 days
          </MenuItem>
          <MenuItem onClick={() => handleExpirationChange(180)}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
            180 days
          </MenuItem>
        </Menu>
      </MinimalToolbar>

      {/* Editor Content */}
      <Box sx={{ display: 'flex' }}>
        {/* Main Editor Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column'
        }}>
          {/* Title, Summary, and Tags - Match CleanTextArea styling */}
          <Box sx={{ 
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            mt: 1,
            padding: '30px 60px 20px 60px',
            borderRadius: '8px',
            boxShadow: isDark 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
            background: isDark ? '#0d1117' : '#ffffff',
            mb: 1,
          }}>
            {/* Title Field */}
            <TextField
              fullWidth
              variant="standard"
              placeholder="Enter title..."
              value={editorTitle || title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{
                mb: 1,
                '& .MuiInput-input': {
                  fontSize: '24px',
                  fontWeight: 600,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  color: isDark ? '#ffffff' : '#1f2937',
                  padding: '4px 0',
                  '&::placeholder': {
                    color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(107, 114, 128, 0.4)',
                  }
                }
              }}
            />
            
            {/* Summary Field */}
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="standard"
              placeholder="Add a brief description..."
              value={selectedSummary || ''}
              onChange={(e) => onSelectSummary?.(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{
                mb: 1,
                '& .MuiInput-input': {
                  fontSize: '14px',
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
                  '&::placeholder': {
                    color: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(107, 114, 128, 0.4)',
                  }
                }
              }}
            />

            {/* Tags */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: '24px' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Tags:
              </Typography>
              
              {selectedTags?.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  variant="outlined"
                  onDelete={() => {
                    const newTags = selectedTags.filter((_, i) => i !== index);
                    onSelectTags?.(newTags);
                  }}
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              ))}
              
              <Box sx={{ minWidth: '80px', height: '24px', display: 'flex', alignItems: 'center' }}>
                {showTagInput ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      size="small"
                      placeholder="Enter tag..."
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newTagInput.trim()) {
                          const updatedTags = [...(selectedTags || []), newTagInput.trim()];
                          onSelectTags?.(updatedTags);
                          setNewTagInput('');
                          setShowTagInput(false);
                        }
                        if (e.key === 'Escape') {
                          setNewTagInput('');
                          setShowTagInput(false);
                        }
                      }}
                      sx={{ width: '80px', '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.25 } }}
                      autoFocus
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setNewTagInput('');
                        setShowTagInput(false);
                      }}
                      sx={{ p: 0.25, fontSize: '0.7rem' }}
                    >
                      ✕
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    size="small"
                    onClick={() => setShowTagInput(true)}
                    sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1, height: '24px' }}
                  >
                    + Add
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
          {/* Edit View */}
          {viewMode === 0 && (
            <CleanTextArea
              ref={textAreaRef}
              value={content || ''}
              onChange={handleContentChange}
              placeholder={getPlaceholder()}
              fontSize={fontSize}
              isDark={isDark}
            />
          )}

          {/* Preview View */}
          {viewMode === 1 && markdownMode && (
            <MarkdownPreview>
              <ReactMarkdown
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={theme.palette.mode === 'dark' ? oneDark : oneLight}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {content || '*Nothing to preview yet. Start writing in the Edit tab.*'}
              </ReactMarkdown>
            </MarkdownPreview>
          )}

        </Box>
      </Box>
    </Box>
  );
}

export default SnippetEditor;
